import { useNavigate } from 'react-router-dom';

import type { AttentionSummary } from '@/api/types';
import { useApi } from '@/api/useApi';
import {
  AvatarStack,
  Caption,
  Card,
  CardHead,
  CountCard,
  ErrorState,
  Loading,
  Page,
  PageHead,
  RateBar,
  Table,
  Tag,
} from '@/components/ui';

/**
 * Screen 01 — today's attention list.
 *
 * An agency with 184 clients cannot read a dashboard of averages. This is a
 * work queue: four counts that each open a list, a client table, and the
 * agency's rates against its own targets. Trend reading lives one level down,
 * where response counts can be shown honestly.
 */
export function AttentionPage() {
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<AttentionSummary>('/agency/attention');

  if (loading) return <Loading />;
  if (error)
    return (
      <Page>
        <ErrorState message={error.message} onRetry={reload} />
      </Page>
    );
  if (!data) return null;

  const { counts } = data;

  return (
    <Page>
      <PageHead
        title="Today's attention list"
        subtitle={`${data.total_clients} clients · ${data.total_caregivers} caregivers`}
      />

      <div className="counts">
        <CountCard
          label="Urgent concerns"
          value={counts.urgent_concerns.count}
          tone={counts.urgent_concerns.count > 0 ? 'urgent' : 'neutral'}
          context={
            counts.urgent_concerns.oldest_minutes !== null
              ? `Oldest waiting ${counts.urgent_concerns.oldest_minutes} min · target 60 min`
              : 'None waiting'
          }
          onClick={() => navigate('/feedback?filter=urgent')}
        />
        <CountCard
          label="Updates to approve"
          value={counts.updates_to_approve.count}
          context="Free-text summaries only"
          onClick={() => navigate('/approvals')}
        />
        <CountCard
          label="Plans unacknowledged"
          value={counts.plans_unacknowledged.count}
          tone={counts.plans_unacknowledged.with_visit_today > 0 ? 'attention' : 'neutral'}
          context={
            counts.plans_unacknowledged.with_visit_today > 0
              ? `${counts.plans_unacknowledged.with_visit_today} have a visit today`
              : 'None with a visit today'
          }
          onClick={() => navigate('/clients')}
        />
        <CountCard
          label="Notes late"
          value={counts.notes_late.count}
          context={
            counts.notes_late.sync_failures > 0
              ? `${counts.notes_late.sync_failures} sync failure`
              : 'None overdue'
          }
          onClick={() => navigate('/notes')}
        />
      </div>

      <div className="split-16">
        <div className="stack stack--tight">
          <Card variant="flush">
            <CardHead
              title="Clients needing attention"
              actions={
                <button type="button" className="btn btn--ghost" onClick={() => navigate('/clients')}>
                  View all {data.total_clients}
                </button>
              }
            />
            <Table
              columns={['Client', 'Care team', 'Needs', 'Confidence']}
              widths={['auto', '104px', '30%', '84px']}
              empty="Nothing needs attention right now."
            >
              {data.clients.map((client) => (
                <tr
                  key={client.id}
                  data-clickable="true"
                  onClick={() => navigate(`/clients/${client.id}/care-team`)}
                >
                  <td>
                    <div className="cell-title">{client.name}</div>
                    <div className="cell-sub">
                      {[client.location, `${client.visits_this_week} visits/wk`]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </td>
                  <td>
                    {/* Coverage depth and single-caregiver risk read without
                        opening anything. */}
                    <AvatarStack people={client.care_team} />
                  </td>
                  <td>
                    <div className="row row--wrap" style={{ gap: 'var(--s-6)' }}>
                      {client.needs.length === 0 ? (
                        <span className="caption">—</span>
                      ) : (
                        client.needs.map((need) => (
                          <Tag
                            key={need.label}
                            label={need.label}
                            tone={need.tone as 'urgent' | 'attention' | 'neutral'}
                          />
                        ))
                      )}
                    </div>
                  </td>
                  <td>
                    {client.confidence.suppressed ? (
                      <span className="caption">{client.confidence.label}</span>
                    ) : (
                      <span className="row" style={{ gap: 'var(--s-4)' }}>
                        <span className="numeric numeric--sm">
                          {client.confidence.score?.toFixed(1)}
                        </span>
                        {client.confidence.trend && client.confidence.trend !== 'steady' ? (
                          <span className="caption" aria-label={`trending ${client.confidence.trend}`}>
                            {client.confidence.trend === 'up' ? '▲' : '▼'}
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
          <Caption>{data.confidence_rule}</Caption>
        </div>

        <Card>
          <div className="stack">
            <h3>Where the agency stands</h3>
            {data.rates.map((rate) => (
              <RateBar
                key={rate.key}
                label={rate.label}
                percent={rate.percent}
                numerator={rate.numerator}
                denominator={rate.denominator}
                target={rate.target}
                meetsTarget={rate.meets_target}
              />
            ))}
            <Caption>
              Every rate is shown against the agency&apos;s own target, with the number it was
              computed from. A percentage without its denominator is not a measurement.
            </Caption>
          </div>
        </Card>
      </div>
    </Page>
  );
}
